import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_IMPORT_RUNS, PATH_IMPORT_BATCHES, PATH_VMS_INTEGRATION_BATCHES } from '@/lib/constants/api'
import getImportRunModel from './models/importRun'
import updateStatus from '@/store/helpers/updateStatus'
import { notifyError, notifySuccess } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

// Shared so Import, Re-check assignments and Re-read file differ only in their wording; each opens
// a fresh Run server-side and returns it.
const postBatchAction = ({ dispatch }, { path, progressMessage, successMessage, failMessage }) => {
  showProgressModal({ dispatch, message: progressMessage })

  return api.doPost({ path })
    .then(response => {
      if (!response || !response.data) {
        throw new Error((response && response.errorMessage) || failMessage)
      }
      notifySuccess(dispatch, successMessage)
      return response.data
    })
    .catch(error => {
      notifyError(dispatch, error.message || failMessage)
      throw error
    })
    .finally(() => {
      hideProgressModal({ dispatch })
    })
}

const importRun = {
  namespaced: true,
  state: {
    ...getImportRunModel()
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    setRunDetail: (state, { run, batches }) => {
      state.run = run
      state.batches = Array.isArray(batches) ? batches : []
    },
    clearRun: (state) => {
      const initial = getImportRunModel()
      Object.keys(initial).forEach(key => { state[key] = initial[key] })
    }
  },
  actions: {
    getRun: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading import, please wait...' })

      return api.doGet({ path: `${PATH_IMPORT_RUNS}/${id}` })
        .then(json => json.data)
        .then(detail => {
          commit('setRunDetail', detail || {})
          commit('updateStatus', { status: COMPLETE })
          return detail
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to load import')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    // The explicit Import: a replay of a Validated Batch with writes enabled, separate from the
    // upload that staged it so hours are never written until the admin has reviewed the result.
    importBatch: (context, { batchId }) => postBatchAction(context, {
      path: `${PATH_VMS_INTEGRATION_BATCHES}/${batchId}/replay`,
      progressMessage: 'Importing, please wait...',
      successMessage: 'Import complete',
      failMessage: 'Unable to import.'
    }),
    // Re-check assignments: the same replay endpoint against an already-imported (Success) Batch,
    // re-resolving its stored Rows against mappings as they now stand so a Row that failed only for
    // an unmapped identifier is repaired once the identifier is seeded, with no re-upload.
    recheckAssignments: (context, { batchId }) => postBatchAction(context, {
      path: `${PATH_VMS_INTEGRATION_BATCHES}/${batchId}/replay`,
      progressMessage: 'Re-checking assignments, please wait...',
      successMessage: 'Assignments re-checked',
      failMessage: 'Unable to re-check assignments.'
    }),
    // Re-read file: re-fetches the stored original, parses it again and rebuilds the Batch's Rows
    // without writing. The only recovery for a Batch that died while being read.
    rereadFile: (context, { batchId }) => postBatchAction(context, {
      path: `${PATH_VMS_INTEGRATION_BATCHES}/${batchId}/reread`,
      progressMessage: 'Re-reading the file, please wait...',
      successMessage: 'File re-read',
      failMessage: 'Unable to re-read the file.'
    }),
    getBatchRows: ({ dispatch }, { batchId, outcomeId }) => {
      return api.doGet({ path: `${PATH_IMPORT_BATCHES}/${batchId}/rows`, parameters: { outcomeId } })
        .then(json => json.data || [])
        .catch(() => {
          notifyError(dispatch, 'Unable to load import rows')
          return []
        })
    },
    clearRun: ({ commit }) => {
      commit('clearRun')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default importRun
